import { saveAs } from 'file-saver';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { MEDIA_MIME_BY_EXT } from 'src/app/shared/constants';

export function hasEulaContent(html: string | null | undefined): boolean {
  if (!html) return false;
  const container = document.createElement('div');
  container.innerHTML = html;
  const text = (container.textContent ?? '').replace(/\u00a0/g, ' ').trim();
  return !!text || !!container.querySelector('img, video, table, a');
}

export function toDocumentHref(path: string): string {
  return `/documents/download?path=${encodeURIComponent(path)}`;
}

function getDocumentPathFromUrl(url: string | null | undefined): string | null {
  if (!url || !url.includes('/documents/download')) return null;
  try {
    return new URL(url, window.location.origin).searchParams.get('path');
  } catch {
    return null;
  }
}

function getFilenameFromPath(path: string): string {
  const segment = path.split('/').pop() || '';
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

export function getFilenameFromUrl(url: string | null | undefined): string {
  const path = getDocumentPathFromUrl(url);
  return path ? getFilenameFromPath(path) : '';
}

export function ensureMime(blob: Blob, file: { name?: string; contentType?: string }): Blob {
  if (blob.type && blob.type !== 'application/octet-stream') return blob;
  const ext = file.name?.split('.').pop()?.toLowerCase() || '';
  const mime = file.contentType || MEDIA_MIME_BY_EXT[ext];
  return mime ? new Blob([blob], { type: mime }) : blob;
}

export function preserveEmptyBlockBreaks(html: string): string {
  if (!html) return html;

  const container = document.createElement('div');
  container.innerHTML = html.replace(/&nbsp;/gi, ' ').replace(/\u00a0/g, ' ');
  container.querySelectorAll('p, h1, h2, h3, h4, h5, h6, li, pre, blockquote, .ql-code-block').forEach((el) => {
    if (el.querySelector('img, video, table, br')) return;
    if ((el.textContent ?? '').trim()) return;
    el.appendChild(document.createElement('br'));
  });
  return container.innerHTML;
}

export function hydrateEulaMedia(html: string, getBlob: (path: string) => Observable<Blob>): Observable<{ html: string; objectUrls: string[]; mappings: { objectUrl: string; path: string }[] }> {
  if (!html) return of({ html: html ?? '', objectUrls: [], mappings: [] });

  const container = document.createElement('div');
  container.innerHTML = html;
  const jobs: Observable<{ objectUrl: string; path: string } | null>[] = [];

  for (const el of Array.from(container.querySelectorAll('img, video')) as (HTMLImageElement | HTMLVideoElement)[]) {
    const path = getDocumentPathFromUrl(el.getAttribute('src'));
    if (!path) continue;

    jobs.push(
      getBlob(path).pipe(
        map((blob) => {
          const objectUrl = URL.createObjectURL(ensureMime(blob, { name: getFilenameFromPath(path) }));
          el.setAttribute('src', objectUrl);
          return { objectUrl, path };
        }),
        catchError(() => of(null))
      )
    );
  }

  return jobs.length === 0 ? of({ html, objectUrls: [], mappings: [] })
                           : forkJoin(jobs).pipe(map((results) => {
                               const mappings = results.filter((item): item is { objectUrl: string; path: string } => !!item);
                               return {
                                 html: container.innerHTML,
                                 objectUrls: mappings.map((item) => item.objectUrl),
                                 mappings
                               };
                             }));
}

export function downloadEulaAttachment(event: MouseEvent, getBlob: (path: string) => Observable<Blob>, onError?: () => void ): void {
  const anchor = (event.target as HTMLElement | null)?.closest?.('a') as HTMLAnchorElement | null;
  const path = getDocumentPathFromUrl(anchor?.getAttribute('href'));
  if (!anchor || !path) return;

  event.preventDefault();
  const name = getFilenameFromPath(path) || anchor.textContent?.trim() || 'download';
  getBlob(path).subscribe({
    next: (blob) => saveAs(ensureMime(blob, { name }), name),
    error: () => onError?.()
  });
}

export function persistEulaContent(html: string, objectUrlToDocumentUrl: Map<string, string>): string {
  let persisted = html;
  objectUrlToDocumentUrl.forEach((documentUrl, objectUrl) => {
    const path = getDocumentPathFromUrl(documentUrl);
    persisted = persisted.split(objectUrl).join(path ? toDocumentHref(path) : documentUrl);
  });
  return preserveEmptyBlockBreaks(persisted.replace(/https?:\/\/[^"'>\s]+(?=\/documents\/download\?path=)/gi, ''));
}
